/**
 * Firma de los comprobantes de retención (bloque 11): la dueña carga la imagen de su firma y el sistema la imprime en
 * «Firma y sello agente de retención». Se guarda en la base y solo la recibe quien puede ver compras: no es un archivo
 * público del sitio. Antes de enviarla se reduce a 600 px de ancho para que pese poco.
 */
import { FIRMA_MAX_BYTES, type EmpresaAgenteDTO } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { EstadoQuery } from '@/componentes/estados.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { get, mensajeDeError, put } from '@/lib/api.ts';

const ANCHO_MAX = 600;

/** Lee la imagen elegida y la devuelve como PNG de a lo sumo 600 px de ancho (data URL). */
function reducir(archivo: File): Promise<string> {
  return new Promise((resolver, rechazar) => {
    const url = URL.createObjectURL(archivo);
    const img = new Image();
    img.onload = () => {
      const escala = Math.min(1, ANCHO_MAX / img.naturalWidth);
      const lienzo = document.createElement('canvas');
      lienzo.width = Math.max(1, Math.round(img.naturalWidth * escala));
      lienzo.height = Math.max(1, Math.round(img.naturalHeight * escala));
      const c = lienzo.getContext('2d');
      URL.revokeObjectURL(url);
      if (!c) return rechazar(new Error('El navegador no pudo preparar la imagen'));
      c.drawImage(img, 0, 0, lienzo.width, lienzo.height);
      resolver(lienzo.toDataURL('image/png'));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      rechazar(new Error('Ese archivo no es una imagen que el navegador pueda leer (usa PNG o JPG)'));
    };
    img.src = url;
  });
}

export function FirmaRetencionesCard() {
  const qc = useQueryClient();
  const entradaArchivo = useRef<HTMLInputElement>(null);
  const agente = useQuery({ queryKey: ['compras', 'empresa'], queryFn: () => get<EmpresaAgenteDTO>('/compras/empresa') });
  const guardar = useMutation({
    mutationFn: (imagen: string | null) => put<{ firma: boolean; bytes: number }>('/consolidado/firma-retenciones', { imagen }),
    onSuccess: (r) => {
      toast.success(r.firma ? 'Firma guardada' : 'Firma quitada', { description: r.firma ? 'Sale en los comprobantes de retención. Queda en auditoría.' : 'Los comprobantes salen con el espacio para firmar a mano.' });
      void qc.invalidateQueries({ queryKey: ['compras', 'empresa'] });
    },
    onError: (e) => toast.error('No se pudo guardar la firma', { description: mensajeDeError(e) }),
  });
  const elegir = async (archivo: File | undefined) => {
    if (!archivo) return;
    try {
      guardar.mutate(await reducir(archivo));
    } catch (e) {
      toast.error('No se pudo leer la imagen', { description: e instanceof Error ? e.message : String(e) });
    } finally {
      if (entradaArchivo.current) entradaArchivo.current.value = '';
    }
  };
  return (
    <Card data-testid="firma-retenciones">
      <CardHeader>
        <div className="flex items-center gap-1"><CardTitle>Firma de los comprobantes de retención</CardTitle><AyudaVentana tema="configuracion.firma" /></div>
        <CardDescription>Se imprime en «Firma y sello agente de retención». Solo la ve quien entra a Compras; no queda como archivo público. PNG o JPG, hasta {FIRMA_MAX_BYTES / 1024} KB.</CardDescription>
      </CardHeader>
      <CardContent>
        <EstadoQuery cargando={agente.isLoading} error={agente.error} datos={agente.data} reintentar={() => void agente.refetch()}>
          {(a) => (
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex h-24 w-56 items-center justify-center rounded-lg border bg-white p-2" data-testid="firma-vista">
                {a.firma ? <img src={a.firma} alt="Firma cargada" className="max-h-full max-w-full" data-testid="firma-imagen" /> : <span className="text-xs text-muted-foreground">Sin firma: se firma a mano</span>}
              </div>
              <div className="flex gap-2">
                <input ref={entradaArchivo} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => void elegir(e.target.files?.[0])} data-testid="firma-archivo" />
                <Button variant="outline" cargando={guardar.isPending} onClick={() => entradaArchivo.current?.click()} data-testid="firma-cargar">{a.firma ? 'Cambiar firma' : 'Cargar firma'}</Button>
                {a.firma ? <Button variant="ghost" disabled={guardar.isPending} onClick={() => guardar.mutate(null)} data-testid="firma-quitar">Quitar</Button> : null}
              </div>
            </div>
          )}
        </EstadoQuery>
      </CardContent>
    </Card>
  );
}
